import React, { useState, useEffect, useRef, useCallback } from "react";
import { Navigate, useNavigate, useOutletContext } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from "react-helmet-async";
import EmlAthnNoSendApi from "Apis/EmlAthnNoSendApi";
import EmlAthnNoVrfRqstApi from "Apis/EmlAthnNoVrfRqstApi";
import EmlAthnNoVrfRqstFirstApi from "Apis/EmlAthnNoVrfRqstFirstApi";
import MbpAthnTknCrtRqstApi from "Apis/MbpAthnTknCrtRqstApi";
import { IdntMthdCdStateChng, AppDispatch } from "Stores/IdntMthdCdReducer";
import useRecaptcha from 'Utils/useRecaptcha';
import EtcCtxtApi from "Apis/EtcCtxtApi";
import { isMobile } from "react-device-detect";
import MbpAthnRsltVrfRqstApi from "Apis/MbpAthnRsltVrfRqstApi";
import zIndex from "@mui/material/styles/zIndex";
import ApiResultModal from "Components/ApiResultModal";

const MembEmlUi = () => {
    // 이메일 자동완성용 도메인 리스트
    const frequencyEmails = [
        '@naver.com',
        '@gmail.com',
        '@nate.com',
        '@daum.net',
        '@hanmail.net',
        '@kakao.com',
        '@icloud.com',
        '@empal.com',
        '@hotmail.com',
        '@outlook.com',
        '@msn.com',
        '@yahoo.com',
    ];
    const [emailList, setEmailList] = useState<string[]>([]);
    const [isDropBox, setIsDropBox] = useState(false);
    const [selected, setSelected] = useState(-1);
    const inputRef = useRef<HTMLDivElement>(null);
    const inputElRef = useRef<HTMLInputElement>(null);
    const [inputWidth, setInputWidth] = useState(0);
    const [inputLeft, setInputLeft] = useState(0);
    const listRef = useRef<HTMLUListElement | null>(null);
    const emlBtnRef = useRef<HTMLButtonElement | null>(null);
    const successNavigateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [emlBtnWidth, setEmlBtnWidth] = useState(0);
    const navigate = useNavigate(); 
    const langData: any = useOutletContext();
    const idntMthdCd = useSelector((state: any) => state.IdntMthdCdState?.IdntMthdCd);
    const dispatch = useDispatch<AppDispatch>();
    const [membEml, setMembEml] = useState("");
    const [authNoVal, setAuthNoVal] = useState("");
    const [emlSendChkLbl, setEmlSendChkLbl] = useState("");
    const [authVerifyChkLbl, setAuthVerifyChkLbl] = useState("");
    const [emlAthnNoTimer, setEmlAthnNoTimer] = useState(0);
    const [membBirthday, setMembBirthday] = useState("");
    const [membBirthdayChkLbl, setMembBirthdayChkLbl] = useState("");
    const [membGender, setMembGender] = useState("");
    const [authStep, setAuthStep] = useState(1); // 1: 생년월일, 2: 성별, 3: 이메일, 4: 인증번호
    const [visibleStep, setVisibleStep] = useState(1);
    const [firstEmlNotiCtxtState, setFirstEmlNotiCtxtState] = useState("");
    const [resultModalOpen, setResultModalOpen] = useState(false);
    const [resultModalMessage, setResultModalMessage] = useState("");
    const [modalAction, setModalAction] = useState<(() => void) | null>(null);
    const { getToken } = useRecaptcha();

    const openResultModal = (message: string, onClose?: () => void) => {
        setResultModalMessage(message);
        setModalAction(() => onClose || null);
        setResultModalOpen(true);
    };

    const closeResultModal = () => {
        setResultModalOpen(false);
        const action = modalAction;
        setModalAction(null);
        if (action) action();
    };

    useEffect(() => {
        return () => {
            if (successNavigateTimerRef.current) {
                clearTimeout(successNavigateTimerRef.current);
                successNavigateTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        setVisibleStep(0);
        const rafId = requestAnimationFrame(() => {
            setVisibleStep(authStep);
        });
        return () => cancelAnimationFrame(rafId);
        
    }, [authStep]);

    const getStepWrapStyle = (step: number) => ({
        opacity: visibleStep >= step ? 1 : 0,
        transform: visibleStep >= step ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
    });

    const isAtLeast12InKst = (yyyymmdd: string) => {
        if (!/^\d{8}$/.test(yyyymmdd)) {
            return { valid: false, allowed: false };
        }

        const year = Number(yyyymmdd.slice(0, 4));
        const month = Number(yyyymmdd.slice(4, 6));
        const day = Number(yyyymmdd.slice(6, 8));

        if (month < 1 || month > 12) {
            return { valid: false, allowed: false };
        }

        const lastDayOfMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
        if (day < 1 || day > lastDayOfMonth) {
            return { valid: false, allowed: false };
        }

        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Asia/Seoul',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(new Date());

        const kstYear = Number(parts.find((p) => p.type === 'year')?.value || '0');
        const kstMonth = Number(parts.find((p) => p.type === 'month')?.value || '0');
        const kstDay = Number(parts.find((p) => p.type === 'day')?.value || '0');

        let age = kstYear - year;
        if (kstMonth < month || (kstMonth === month && kstDay < day)) {
            age -= 1;
        }

        return { valid: true, allowed: age >= 12 };
    };

    // 타이머 감소 useEffect (EmlAthnPopUi 참고)
    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;
        if (emlAthnNoTimer > 0) {
            timer = setInterval(() => {
                setEmlAthnNoTimer((prev) => {
                    const newTime = prev - 1000;
                    if (newTime <= 0) {
                        return 0;
                    }
                    return newTime;
                });
            }, 1000);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [emlAthnNoTimer]);

    // 분:초 포맷
    const timerMinutes = String(Math.floor((emlAthnNoTimer / (1000 * 60)) % 60)).padStart(2, '0');
    const timerSecond = String(Math.floor((emlAthnNoTimer / 1000) % 60)).padStart(2, '0');

    const openPrntAthnWindow = async () => {
        try {
            const tokenResp = await MbpAthnTknCrtRqstApi("PRNT_ATHN", null);
            if (tokenResp.apiHdr.code !== "ATHN0000") {
                openResultModal(`${tokenResp.apiHdr.msg}]`);
                return;
            }

            const reqKMCISForm = document.createElement("form");
            reqKMCISForm.method = "post";
            reqKMCISForm.action = "https://www.kmcert.com/kmcis/web/kmcisReq.jsp";
            reqKMCISForm.style.display = "none";

            const trCertInput = document.createElement("input");
            trCertInput.type = "hidden";
            trCertInput.name = "tr_cert";
            trCertInput.value = tokenResp.apiBody.kmcTkn;

            const trUrlInput = document.createElement("input");
            trUrlInput.type = "hidden";
            trUrlInput.name = "tr_url";
            trUrlInput.value = `${process.env.REACT_APP_KMC_REDIRECT_URL}`;

            const trVerInput = document.createElement("input");
            trVerInput.type = "hidden";
            trVerInput.name = "tr_ver";
            trVerInput.value = "V2";

            reqKMCISForm.appendChild(trCertInput);
            reqKMCISForm.appendChild(trUrlInput);
            reqKMCISForm.appendChild(trVerInput);
            document.body.appendChild(reqKMCISForm);

            if (isMobile === true) {
                reqKMCISForm.target = "";
            } else {
                const KMCIS_window = window.open('', 'KMCISWindow', 'width=425, height=550, resizable=0, scrollbars=no, status=0, titlebar=0, toolbar=0, left=435, top=250');
                if (KMCIS_window == null) {
                    document.body.removeChild(reqKMCISForm);
                    return;
                }
                reqKMCISForm.target = "KMCISWindow";
            }

            reqKMCISForm.submit();
            document.body.removeChild(reqKMCISForm);
        } catch (error: any) {
            openResultModal(error?.message || "법정대리인 본인인증 요청 중 오류가 발생했습니다.");
        }
    };

    useEffect(() => {
        function kmcRespFunc(kmcApiTknrslt:string, kmcApiCertNumrslt:string) {
            MbpAthnRsltVrfRqstApi(kmcApiTknrslt, kmcApiCertNumrslt)
            .then((resp) => {
                if(resp.apiHdr.code === "ATHN0000") {
                    openResultModal("법정 대리인 인증이 완료되었습니다.");
                    dispatch(IdntMthdCdStateChng('EML'));
                    return;
                }
                openResultModal(`${resp.apiHdr.msg}`);
            })
            .catch((error:any) => {
                openResultModal(error?.message || "본인인증 검증 중 오류가 발생했습니다.");
            });
        }

        window.kmcRespFunc = kmcRespFunc;

        return () => {
            try { window.kmcRespFunc = undefined; } catch (e) {}
        }
    }, []);


    // 이메일 인증번호 발송
    const handleSendAuthNo = async () => {
        if (!/^[a-zA-Z0-9+_.-]+@[a-zA-Z0-9.-]+$/.test(membEml)) {
            setEmlSendChkLbl(langData && langData.MYPAGE_EML_INVALID_FORMAT_00 || "올바르지 않은 이메일 형식입니다.");
            return;
        }
        let token = '';
        try {
            token = await getToken('send_email');
        } catch (err) {
            setEmlSendChkLbl(langData && langData.COMM_CPTCH_ERROR_DESC_00 || 'reCAPTCHA 실행 실패. 잠시 후 다시 시도해주세요.');
            return;
        }
        const resp = await EmlAthnNoSendApi(membEml, "FIRST_EML", token);
        switch (resp.apiHdr.code) {
            case "ATHN0000":
                setEmlAthnNoTimer(resp.apiBody.exprTime * 1000);
                setEmlSendChkLbl("");
                setAuthVerifyChkLbl("");
                setAuthStep(4); // 인증번호 입력 단계로 전환
                break;
            case "COMM3004":
                setEmlSendChkLbl(langData && langData.MYPAGE_EML_ALREADY_USED_00 || "이미 사용중인 이메일입니다.");
            break;
            case "ATHN2303":
                navigate("/Login");
            break;
            default:
                setEmlSendChkLbl(langData && langData.MYPAGE_EML_SEND_FAIL_00 || "인증번호 발송 실패");
            break;
        }   
        // token was local; nothing to clear here
    };

    // 인증번호 검증
    const handleVerifyAuthNo = async () => {
        if (!/^[0-9]{6}$/.test(authNoVal)) {
            setAuthVerifyChkLbl(langData && langData.MYPAGE_ATHN_NO_INVALID_00 || "인증번호가 올바르지 않습니다.");
            return;
        }
        let token = '';
        try {
            token = await getToken('verify_email');
        } catch (err) {
            setAuthVerifyChkLbl(langData && langData.COMM_CPTCH_ERROR_DESC_00 || 'reCAPTCHA 실행 실패. 잠시 후 다시 시도해주세요.');
            return;
        }
        const resp = await EmlAthnNoVrfRqstFirstApi(authNoVal, token, membBirthday, membGender);
        switch (resp.apiHdr.code) {
            case "ATHN0000":
                setAuthVerifyChkLbl("인증 성공");
                // 인증 성공 시 Redux `idntMthdCd`를 'EML'로 갱신하여 컴포넌트가 리렌더되도록 함
                dispatch(IdntMthdCdStateChng('EML'));
                if (successNavigateTimerRef.current) {
                    clearTimeout(successNavigateTimerRef.current);
                }
                successNavigateTimerRef.current = setTimeout(() => {
                    navigate('/MyPage');
                }, 10000);
                break;
            case "ATHN2128":
                openResultModal(
                    langData && langData.MYPAGE_CHILD_PRNT_CONSENT_RQST_00 || "미성년자의 경우 법정 대리인 동의가 필요합니다.",
                    () => { openPrntAthnWindow(); }
                );
                break;
            default:
                setAuthVerifyChkLbl(langData && langData.MYPAGE_ATHN_NO_INVALID_00 || "인증번호가 올바르지 않습니다.");
                break;
        }
        // token was local; nothing to clear here
    };
    // 외부 클릭 시 드롭박스 닫기
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
                setIsDropBox(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // inputWrap 실제 너비 측정 (wrapper 기준)
    useEffect(() => {
        const updateWidth = () => {
            const el = inputRef.current;
            const width = el?.offsetWidth || 0;
            const left = el ? el.offsetLeft : 0;
            setInputWidth(width);
            setInputLeft(left);
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    // measure send button width so timer can be positioned at button-left - 10px
    useEffect(() => {
        const updateBtn = () => {
            const w = emlBtnRef.current?.offsetWidth || 0;
            setEmlBtnWidth(w);
        };
        updateBtn();
        window.addEventListener('resize', updateBtn);
        return () => window.removeEventListener('resize', updateBtn);
    }, [emlBtnRef.current, emlAthnNoTimer]);
    useEffect(() => {
        EtcCtxtApi("CTNT_KIND_ETC","FIRST_EML_GUIDE","BBO").then((resp)=>{
            
            if(resp.apiHdr.code === "COMM0000") {
                setFirstEmlNotiCtxtState(resp.apiBody.ctxt);
            }
        });
    },[]);
    

    // 키보드로 선택 위치가 변경되면 해당 항목이 보이도록 스크롤
    useEffect(() => {
        if (!listRef.current) return;
        if (selected < 0) return;
        const el = listRef.current.children[selected] as HTMLElement | undefined;
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'nearest' });
        }
    }, [selected]);
return (
        <>
            <ApiResultModal
                open={resultModalOpen}
                message={resultModalMessage}
                onClose={closeResultModal}
                confirmText={langData && langData.COMM_MAIN_CHK_MAIN_BTN_00}
            />
            <Helmet>
                <title>{`${process.env.REACT_APP_TITLE_PREFIX} ${langData && langData.MYPAGE_MENU_EML_CHNG_BTN_00}`}</title>
            </Helmet>
            <div className="container1300 BgW pb200">
                <div className="myPageSubTitlWrap">
                    <p className="myPageCtxtTitl">{langData?.MYPAGE_EML_ATHN_PAGE_TITL_01}</p>
                </div>
                {/* 페이지 하단 이메일 인증 UI */}
                <div style={{display:"flex",marginTop:"70px",justifyContent:"center",alignItems:"center",gap:"90px"}} className="myPageEmailAuthFooterWrap">
                    <div>
                        <img src={process.env.PUBLIC_URL + "/images/cbt/emlReward.png"} alt=""  style={{maxWidth:"100%"}}/>
                    </div>
                    <div className="myPageEmailAuthFooter">
                        {idntMthdCd === 'NA' ? (
                            <>
                                <h3 style={{ fontSize: 18, marginBottom: 12 }}>
                                    {langData?.MYPAGE_EML_ATHN_MAIN_TITL_00 || "CBT 기간 한정! 이메일 인증 보상"}
                                </h3>
                                <p>
                                    {(String(langData?.MYPAGE_EML_ATHN_MAIN_DESC_00 || '')
                                        .split(/<br\s*\/?\s*>/gi)
                                        .map((line, i, arr) => (
                                            <React.Fragment key={i}>
                                                {line}
                                                {i < arr.length - 1 && <br />}
                                            </React.Fragment>
                                        )))}
                                </p>
                            </>
                        ) : (
                            <>
                                <h3 style={{ fontSize: 18, marginBottom: 12 }}>
                                    {langData?.MYPAGE_EML_ATHN_DONE_TITL_00 || "이메일 인증이 완료되었습니다!"}
                                </h3>
                                <p>
                                    {(String(langData?.MYPAGE_EML_ATHN_DONE_DESC_00 || '')
                                        .split(/<br\s*\/?\s*>/gi)
                                        .map((line, i, arr) => (
                                            <React.Fragment key={i}>
                                                {line}
                                                {i < arr.length - 1 && <br />}
                                            </React.Fragment>
                                        )))}
                                </p>
                            </>
                        )}
                        {idntMthdCd === 'NA' && (
                            <>
                        {/* Step 1: 생년월일 입력 */}
                        <div style={{marginTop:"30px"}}>
                            <div className="authStepTitl">
                                <h2>{langData?.MEMB_JOIN_DOB_MAIN_TITL_00 || "생년월일"}</h2>
                                <span>
                                    {langData?.MEMB_JOIN_DOB_MAIN_DESC_00 || "※ 필수 항목입니다."}
                                </span>
                            </div>
                            {authStep >= 1 && (
                            <div className={`emailInputWrap mt10 ${authStep > 1 ? 'disabled' : ''}`} style={{ ...getStepWrapStyle(1), marginBottom: 12, position: 'relative' }}>
                                {authStep === 1 ? (
                                    <input
                                        type="text"
                                        placeholder={langData?.MYPAGE_BIRTH_INPUT_DESC_00 || "생년월일을 입력하세요 (예: 19900101)"}
                                        value={membBirthday}
                                        onChange={e => {
                                            const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
                                            setMembBirthday(val);
                                            setMembBirthdayChkLbl("");
                                            if (val.length === 8) {
                                                const ageCheck = isAtLeast12InKst(val);
                                                if (!ageCheck.valid) {
                                                    setMembBirthdayChkLbl("생년월일 형식이 올바르지 않습니다.");
                                                    return;
                                                }
                                                if (!ageCheck.allowed) {
                                                    setMembBirthdayChkLbl("만12세미만은 가입불가");
                                                    return;
                                                }
                                                setAuthStep(2); // 생년월일 입력 완료 시 단계 2로 진행
                                            }
                                        }}
                                        maxLength={8}

                                    />
                                ) : (
                                    <div>
                                        {membBirthday}
                                    </div>
                                )}
                                {membBirthdayChkLbl && (
                                    <p className="emlDupChkLbl" style={{
                                        color: '#e74c3c',
                                        minHeight: 20,
                                        position: 'absolute',
                                        left: 0,
                                        top: '100%',
                                        margin: '2px 0 0 0',
                                        fontWeight: 400,
                                        fontSize: '13px',
                                        zIndex: 9,
                                        padding: '0 4px'
                                    }}>{membBirthdayChkLbl}</p>
                                )}
                            </div>
                            )}

                        </div>

                        {/* Step 2: 성별 선택 */}
                        {authStep >= 2 && (
                        <div>
                            <div className="authStepTitl">
                                <h2>{langData?.MEMB_JOIN_GNDR_MAIN_LBL_00 || "성별"}</h2>
                                <span>
                                    {langData?.MEMB_JOIN_DOB_MAIN_DESC_00 || "※ 필수 항목입니다."}
                                </span>
                            </div>
                            <div className={`emailInputWrap genderChkWrap mt10 ${authStep > 2 ? 'disabled' : ''}`} style={{ ...getStepWrapStyle(2), marginBottom: 12, position: 'relative' }}>
                                {authStep === 2 ? (
                                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                            <div>
                                                <input
                                                    type="checkbox"
                                                    name="membGenderMale"
                                                    className="agreChkBox"
                                                    id="membGenderMale"
                                                    checked={membGender === 'M'}
                                                    onChange={e => {
                                                        setMembGender(e.target.checked ? 'M' : '');
                                                        if (e.target.checked) {
                                                            setAuthStep(3); // 성별 선택 시 단계 3으로 진행
                                                        }
                                                    }}
                                                />
                                                <label htmlFor="membGenderMale">{langData?.MYPAGE_GENDER_MALE_DESC_00 || "남성"}</label>
                                            </div>
                                            <div>
                                                <input
                                                    type="checkbox"
                                                    name="membGenderFemale"
                                                    className="agreChkBox"
                                                    id="membGenderFemale"
                                                    checked={membGender === 'F'}
                                                    onChange={e => {
                                                        setMembGender(e.target.checked ? 'F' : '');
                                                        if (e.target.checked) {
                                                            setAuthStep(3); // 성별 선택 시 단계 3으로 진행
                                                        }
                                                    }}
                                                />
                                                <label htmlFor="membGenderFemale">{langData?.MYPAGE_GENDER_FEMALE_DESC_00 || "여성"}</label>
                                            </div>
                                        </div>
                                ) : (
                                    <div >
                                        {membGender === 'M' ? (langData?.MYPAGE_GENDER_MALE_DESC_00 || "남성") : (langData?.MYPAGE_GENDER_FEMALE_DESC_00 || "여성")}
                                    </div>
                                )}
                            </div>
                        </div>
                        )}

                        {/* Step 3: 이메일 입력 */}
                        {authStep >= 3 && (
                        <div className={`emailInputWrap email-with-auth ${authStep > 3 ? 'disabled' : ''}`} ref={inputRef} style={{ ...getStepWrapStyle(3), marginBottom: 12, position: 'relative' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{ flex: 1 }}>
                                    {authStep === 3 ? (
                                        <>
                                            <input
                                                type="text"
                                                placeholder={langData?.EML_ATHN_EML_INPUT_DESC_00 || "이메일을 입력하세요"}
                                                value={membEml}
                                                ref={inputElRef}
                                                style={{ boxSizing: 'border-box', width: '100%', minWidth:'300px' }}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setMembEml(val);
                                                    if (val.length > 0) {
                                                        let filtered: string[] = [];
                                                        const atIdx = val.indexOf('@');
                                                        if (atIdx > -1) {
                                                            const prefix = val.slice(0, atIdx);
                                                            const domainInput = val.slice(atIdx + 1).toLowerCase();
                                                            filtered = frequencyEmails
                                                                .filter(domain => domain.replace('@', '').startsWith(domainInput))
                                                                .map(domain => prefix + domain);
                                                        } else {
                                                            filtered = frequencyEmails.map(domain => val + domain);
                                                        }
                                                        setEmailList(filtered);
                                                        setIsDropBox(filtered.length > 0);
                                                    } else {
                                                        setIsDropBox(false);
                                                        setSelected(-1);
                                                    }
                                                }}
                                                onKeyUp={e => {
                                                    if (isDropBox) {
                                                        if (e.key === 'ArrowDown' && emailList.length - 1 > selected) {
                                                            setSelected(selected + 1);
                                                        } else if (e.key === 'ArrowUp' && selected > 0) {
                                                            setSelected(selected - 1);
                                                        } else if (e.key === 'Enter' && selected >= 0) {
                                                            setMembEml(emailList[selected]);
                                                            setIsDropBox(false);
                                                            setSelected(-1);
                                                        }
                                                    }
                                                }}
                                                autoComplete="off"
                                            />
                                            {isDropBox && emailList.length > 0 && (
                                                <ul
                                                    ref={listRef}
                                                    style={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        right: 0,
                                                        top: '100%',
                                                        background: '#fff',
                                                        border: '1px solid #ddd',
                                                        width: '100%',
                                                        zIndex: 10,
                                                        margin: 0,
                                                        padding: 0,
                                                        listStyle: 'none',
                                                        maxHeight: 180,
                                                        overflowY: 'auto',
                                                        boxSizing: 'border-box'
                                                    }}
                                                >
                                                    {emailList.map((item, idx) => (
                                                        <li
                                                            key={item}
                                                            onMouseOver={() => setSelected(idx)}
                                                            onMouseDown={e => {
                                                                e.preventDefault();
                                                                setMembEml(item);
                                                                setSelected(-1);
                                                                setIsDropBox(false);
                                                            }}
                                                            style={{
                                                                padding: '6px 12px',
                                                                background: selected === idx ? '#f5f5f5' : '#fff',
                                                                color: selected === idx ? '#0078d4' : '#222',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            {item}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </>
                                    ) : (
                                        <div>
                                            {membEml}
                                        </div>
                                    )}
                                </div>
                                <div className="eml-send-wrap" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                                    {emlAthnNoTimer > 0 ? (
                                        <>
                                            <button
                                                ref={el => { emlBtnRef.current = el }}
                                                className="eml-send-btn"
                                                type="button"
                                                onClick={handleSendAuthNo}
                                                style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex:"2"}}
                                            >
                                                {langData?.EML_ATHN_NO_SEND_BTN_01 || "다시 받기"}
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            ref={el => { emlBtnRef.current = el }}
                                            className="eml-send-btn"
                                            type="button"
                                            onClick={handleSendAuthNo}
                                            style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' ,zIndex:"2"} }
                                        >
                                            {langData?.EML_ATHN_NO_SEND_BTN_00 || "인증번호 받기"}
                                        </button>
                                    )}
                                </div>
                            </div>
                            {emlSendChkLbl && (
                                <p className="emlDupChkLbl" style={{
                                    color: '#e74c3c',
                                    minHeight: 20,
                                    position: 'absolute',
                                    left: 0,
                                    top: '100%',
                                    margin: '2px 0 0 0',
                                    fontWeight: 400,
                                    fontSize: '13px',
                                    zIndex: 9,
                                    padding: '0 4px'
                                }}>{emlSendChkLbl}</p>
                            )}
                        </div>
                        )}

                        {/* Step 4: 인증번호 입력 */}
                        {authStep >= 4 && (
                        <div className="emailInputWrap mt10 email-with-auth" style={{ ...getStepWrapStyle(4), marginBottom: 12, position: 'relative' }}>
                            {authStep === 4 && (
                            <div>
                                <input
                                    type="text"
                                    placeholder={langData?.MYPAGE_ATHN_NO_INPUT_DESC_00 || "인증번호를 입력하세요"}
                                    value={authNoVal}
                                    onChange={e => setAuthNoVal(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                                    maxLength={6}
                                    style={{ width: '100%', boxSizing: 'border-box' }}
                                />
                                {authVerifyChkLbl && (
                                    <p className="emlDupChkLbl" style={{
                                        color: authVerifyChkLbl === '인증 성공' ? '#2a7' : '#e74c3c',
                                        minHeight: 20,
                                        position: 'absolute',
                                        left: 0,
                                        top: '100%',
                                        margin: '2px 0 0 0',
                                        fontWeight: 400,
                                        fontSize: '13px',
                                        zIndex: 9,
                                        padding: '0 4px'
                                    }}>{authVerifyChkLbl}</p>
                                )}
                                <span
                                    className="timer eml-timer"
                                    style={{
                                        position: 'absolute',
                                        right: 0,
                                        margin:'0 10px',
                                        color: '#f55',
                                        fontWeight: 600,
                                        zIndex: 60
                                    }}
                                >
                                    {timerMinutes}:{timerSecond}
                                </span>
                            </div>
                            )}
                        </div>
                        )}
                        
                        {authStep >= 4 && (
                        <button
                            className="athnBtn"
                            type="button"
                            onClick={handleVerifyAuthNo}
                        >
                            {langData?.	MYPAGE_EML_ATHN_NO_BTN_00 || "인증 완료하고 혜택 받기"}
                        </button>
                        )}
                        </>)
                        }
                    </div>
                </div>
                {/* 인증 안내 아코디언 메뉴 */}
                <div className="emailAuthAccordion mt50">
                    <details>
                        <summary>
                            <svg className="chev" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="#222"/>
                            </svg>
                            <span>{langData?.EML_ATHN_ACDN_TITL_00 || "주의사항"}</span>
                        </summary>
                        <div 
                            dangerouslySetInnerHTML={{
                                __html: firstEmlNotiCtxtState,
                        }}>

                        </div>

                    </details>
                </div>
            </div>
    		</>
    );
};

export default MembEmlUi;